import { format, parseISO, isValid, isBefore, intervalToDuration, formatRelative, startOfToday } from 'date-fns';

const parseDisplayDate = (dateString: string): Date => {
	if (!dateString.includes('T')) return parseISO(dateString);
	return parseISO(normalizeIso(dateString) ?? dateString);
};

/**
 * Formats dates responsively based on screen size
 * Uses short format (YY-MM-dd) for mobile and full format (yyyy-MM-dd) for desktop
 */
export const formatDateResponsive = (dateString: string | null | undefined, isMobile?: boolean): string => {
	if (!dateString) return '';
	try {
		const date = parseISO(dateString);
		if (!isValid(date)) return dateString;
		return format(date, isMobile ? 'yy-MM-dd' : 'yyyy-MM-dd');
	} catch (error) {
		console.warn('Error formatting date:', error);
		return dateString;
	}
};

export const formatOnlyYear = (dateString: string | null | undefined): string => {
	if (!dateString) return '';
	try {
		const date = parseDisplayDate(dateString);
		if (!isValid(date)) return dateString;
		return format(date, 'yyyy');
	} catch (error) {
		console.warn('Error formatting year:', error);
		return dateString;
	}
};

/**
 * Formats a date string in a readable format
 * Example output: "25 June 2025"
 */
export const formatDate = (dateString: string | null | undefined): string => {
	if (!dateString) return 'N/A';
	try {
		const date = parseDisplayDate(dateString);
		if (!isValid(date)) return dateString;
		return format(date, 'd MMMM yyyy');
	} catch (error) {
		console.warn('Error formatting date:', error);
		return dateString;
	}
};

/** Formats a date with a short month name. Example output: "25 Jun 2025" */
export const formatShortDate = (dateString: string | null | undefined): string => {
	if (!dateString) return 'N/A';
	try {
		const date = parseDisplayDate(dateString);
		if (!isValid(date)) return dateString;
		return format(date, 'd MMM yyyy');
	} catch (error) {
		console.warn('Error formatting short date:', error);
		return dateString;
	}
};

/**
 * Formats a full date-time string deterministically using date-fns
 * Example output: 2025-09-04 10:20:00
 */
export const formatDateTime = (iso?: string | null) => {
	if (!iso) return '-';
	try {
		const d = parseISO(iso);
		if (!isValid(d)) return '-';
		return format(d, 'yyyy-MM-dd HH:mm:ss');
	} catch {
		return '-';
	}
};

/** Whether a date string names a moment rather than a day: `2026-08-18T14:15:00Z`, not `2026-08-18`. */
export const hasTimeOfDay = (dateString: string): boolean => dateString.includes('T');

/**
 * A moment in full, for wherever the short form is on the line and the exact one is on demand.
 * Example output: "Tuesday 18 August 2026, 2:15 pm". A date with no time of day formats as the date alone.
 */
export const formatLongDateTime = (dateString: string | null | undefined): string => {
	if (!dateString) return 'N/A';
	try {
		const date = parseDisplayDate(dateString);
		if (!isValid(date)) return dateString;
		return format(date, hasTimeOfDay(dateString) ? 'EEEE d MMMM yyyy, h:mm aaa' : 'EEEE d MMMM yyyy');
	} catch (error) {
		console.warn('Error formatting long date-time:', error);
		return dateString;
	}
};

export const formatLocalDateForApi = (date = new Date()): string => format(date, 'yyyy-MM-dd');

export const pluralize = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`;

/**
 * Human-friendly time left (e.g., "6 days left") using date-fns intervalToDuration
 * @param iso - ISO date string
 * @param detailed - If true, shows multiple dimensions (e.g., "1 day, 22 hours, 30 minutes left")
 */
export const timeLeft = (iso?: string | null, detailed = false) => {
	if (!iso) return null;
	const expires = parseISO(iso);
	if (!isValid(expires)) return null;
	const now = new Date();
	if (isBefore(expires, now)) return null;
	const {
		days = 0,
		hours = 0,
		minutes = 0,
	} = intervalToDuration({
		start: now,
		end: expires,
	});

	function _timeLeft() {
		if (detailed) {
			const parts: string[] = [];
			if (days > 0) parts.push(pluralize(days, 'day'));
			if (hours > 0) parts.push(pluralize(hours, 'hour'));
			if (minutes > 0 && days === 0) parts.push(pluralize(minutes, 'minute'));
			if (parts.length > 0) {
				return `${parts.join(', ')}`;
			}
		}
		if (days > 0) return `${pluralize(days, 'day')}`;
		if (hours > 0) return `${pluralize(hours, 'hour')}`;
		if (minutes > 0) return `${pluralize(minutes, 'minute')}`;
		return '< 1 minute';
	}
	return `${_timeLeft()} left`;
};

export const formatRelativeDate = (iso?: string | null) => {
	if (!iso) return '-';
	const date = parseISO(iso);
	if (!isValid(date)) return '-';
	return formatRelative(date, new Date());
};

/**
 * Formats a date as a short, concise string suitable for notifications
 * Examples: "20m", "1h", "2d", "just now"
 *
 * Why: Notification timestamps should be brief and not take up much space,
 * while still being informative about how recent the notification is.
 */
export const formatNotificationDate = (iso?: string | null): string => {
	if (!iso) return '-';
	const date = parseISO(iso);
	if (!isValid(date)) return '-';

	const now = new Date();
	const diffMs = now.getTime() - date.getTime();

	// Handle future dates (shouldn't happen for notifications, but be safe)
	if (diffMs < 0) return 'just now';

	const seconds = Math.floor(diffMs / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);
	const weeks = Math.floor(days / 7);
	const months = Math.floor(days / 30);
	const years = Math.floor(days / 365);

	// Less than a minute
	if (minutes < 1) return 'just now';

	// Less than an hour
	if (hours < 1) return `${minutes}m`;

	// Less than a day
	if (days < 1) return `${hours}h`;

	// Less than a week
	if (weeks < 1) return `${days}d`;

	// Less than a month
	if (months < 1) return `${weeks}w`;

	// Less than a year
	if (years < 1) return `${months}mo`;

	// A year or more
	return `${years}y`;
};

/**
 * Date utilities to safely parse and compare ISO-like timestamps coming from the API.
 *
 * Why: Some API fields may include microseconds (more than 3 fractional digits)
 * or omit a timezone suffix. JS Date parsing is inconsistent for those cases.
 * We normalize to RFC3339-compatible strings in UTC before parsing.
 */

/**
 * Normalizes an ISO-like timestamp to an RFC3339 string parsable by Date.
 * - Truncates fractional seconds to 3 digits (milliseconds)
 * - Appends 'Z' if no timezone offset is present, treating it as UTC
 */
export const normalizeIso = (input?: string | null): string | null => {
	if (!input) return null;
	const s = input.trim();
	if (!s) return null;

	// Split off timezone if present
	const tzMatch = s.match(/([zZ]|[+-]\d{2}:?\d{2})$/);
	const hasTz = !!tzMatch;

	// Separate main part and timezone
	let main = s;
	let tz = '';
	if (hasTz && tzMatch) {
		const idx = s.lastIndexOf(tzMatch[0]);
		main = s.slice(0, idx);
		tz = tzMatch[0];
	}

	// Handle fractional seconds: .123456 -> .123
	main = main.replace(/(\.\d{1,})(?!\d)/, (m) => {
		// Exact fractional only at the end of main string
		return m.length > 4 ? m.slice(0, 4) : m; // keep dot + 3 digits
	});

	// If there is a fractional part embedded before timezone, ensure max 3 digits too
	main = main.replace(/\.(\d{3,})/, (_, frac: string) => `.${frac.slice(0, 3)}`);

	// Reattach timezone or add Z if missing
	if (!hasTz) tz = 'Z';

	return `${main}${tz}`;
};

/**
 * Parses an ISO-like timestamp into a Date, after normalization.
 */
export const parseIsoToDate = (input?: string | null): Date | null => {
	const n = normalizeIso(input);
	if (!n) return null;
	const d = new Date(n);
	return isNaN(d.getTime()) ? null : d;
};

/**
 * Converts an ISO-like timestamp into a numeric timestamp (ms since epoch), or 0 if invalid.
 */
export const toTimestamp = (input?: string | null): number => {
	const d = parseIsoToDate(input);
	return d ? d.getTime() : 0;
};

/**
 * Returns true if a >= b when comparing ISO-like timestamps, using UTC normalization.
 */
export const isOnOrAfter = (a?: string | null, b?: string | null): boolean => {
	const ta = toTimestamp(a);
	const tb = toTimestamp(b);
	if (tb === 0) return ta !== 0; // if baseline invalid, treat any valid a as after
	return ta >= tb;
};

/**
 * Calculates how long ago a task was due
 */
export const getDueAgoText = (dueDate: string): string => {
	const today = startOfToday();
	const due = parseISO(dueDate);
	const diffInDays = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));

	if (diffInDays === 0) return 'Today';
	if (diffInDays === 1) return '1d ago';
	if (diffInDays < 7) return `${diffInDays}d ago`;
	if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;
	if (diffInDays <= 364) return `${Math.floor(diffInDays / 30)}mo ago`;
	return `${Math.floor(diffInDays / 365)}y ago`;
};
